import { useEffect, useState } from "react";
import axios from "axios";
import {
  Loader2, RefreshCw, Radio, Users, Sparkles, Eye, Trash2,
  Star, Brain, MapPin, X, MessageSquareText, Calendar, Hourglass,
  Power, RotateCcw, Ban,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { VidenteClosedSessionsSection } from "./VidenteClosedSessionsSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMin = (m) => {
  if (!m) return "";
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const r = m % 60;
    return r ? `${h}h ${r}min` : `${h}h`;
  }
  return `${m}min`;
};

/**
 * Admin monitor for Vidente Virtual consultations.
 *
 * Top-level stats: users enabled, total grants, total sessions, active now.
 * Per-user table: row per user with vidente access ever granted.
 * Detail modal: full dossier (profile + sessions + messages + ratings + grants history)
 * with a "🧠 Reset memory" button to delete the accumulated spiritual profile.
 */
export function AdminVidenteMonitorTab() {
  const { authHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // user_id
  const [dossier, setDossier] = useState(null);
  const [dossierLoading, setDossierLoading] = useState(false);
  const [globalEnabled, setGlobalEnabled] = useState(null); // null = loading

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/vidente/monitor`, { headers: authHeader() });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando monitor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/vidente/settings`, { headers: authHeader() });
        if (!cancelled) setGlobalEnabled(!!r.data?.enabled);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleGlobal = async () => {
    const next = !globalEnabled;
    if (!next && !window.confirm("¿Desactivar el Vidente Virtual para TODOS los usuarios? Nadie podrá usarlo hasta que lo reactives.")) return;
    try {
      await axios.put(`${API}/admin/vidente/settings`, { enabled: next }, { headers: authHeader() });
      setGlobalEnabled(next);
      toast.success(next ? "🟢 Vidente Virtual ACTIVADO globalmente" : "🔴 Vidente Virtual DESACTIVADO globalmente");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const resetTimer = async (u) => {
    const def = u.pending_minutes || 60;
    const minStr = window.prompt(
      `⏱ Reiniciar tiempo de Vidente Virtual para ${u.name || u.username}\n\n` +
      `Minutos de acceso (el contador inicia cuando hable con la IA por primera vez):`,
      String(def)
    );
    if (minStr === null) return;
    const minutes = Number(minStr);
    if (Number.isNaN(minutes) || minutes <= 0) return toast.error("Minutos inválidos");
    try {
      await axios.post(
        `${API}/admin/vidente/user/${u.user_id}/timer/reset`,
        { minutes: Math.round(minutes) },
        { headers: authHeader() }
      );
      toast.success(`⏱ Timer re-armado: ${fmtMin(Math.round(minutes))} — inicia con su próxima interacción`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const cancelTimer = async (u) => {
    if (!window.confirm(`¿Cancelar el acceso de ${u.name || u.username} al Vidente Virtual ahora mismo?`)) return;
    try {
      await axios.post(`${API}/admin/vidente/user/${u.user_id}/timer/cancel`, {}, { headers: authHeader() });
      toast.success("Acceso cancelado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const deleteSession = async (sid, userId) => {
    if (!window.confirm("¿Eliminar esta sesión y toda su conversación de forma permanente?")) return;
    try {
      await axios.delete(`${API}/admin/vidente/sessions/${sid}`, { headers: authHeader() });
      toast.success("Sesión eliminada");
      if (selected === userId) openDossier(userId);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/vidente/monitor`, { headers: authHeader() });
        if (!cancelled) setData(r.data);
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [authHeader]);

  // Real-time refresh whenever any vidente event fires (new message, session, rating, etc)
  useRealtimeEvents({
    admin_refresh: (payload) => {
      if (payload?.scope === "vidente" || payload?.scope === "users") {
        load();
      }
    },
  });

  const openDossier = async (userId) => {
    setSelected(userId);
    setDossierLoading(true);
    setDossier(null);
    try {
      const r = await axios.get(`${API}/admin/vidente/user/${userId}/dossier`, { headers: authHeader() });
      setDossier(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error abriendo dossier");
    } finally {
      setDossierLoading(false);
    }
  };

  const resetMemory = async (userId) => {
    if (!window.confirm("¿Borrar toda la memoria espiritual de CrisAngel Astral sobre este usuario? Esta acción es irreversible y la próxima sesión empezará desde cero.")) return;
    try {
      await axios.delete(`${API}/admin/spiritual-profile/${userId}`, { headers: authHeader() });
      toast.success("Memoria reseteada");
      if (selected === userId) {
        openDossier(userId);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  if (loading && !data) {
    return <div className="text-center py-10 text-neutral-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Cargando monitor…</div>;
  }
  if (!data) return null;

  const { stats, users } = data;

  return (
    <div data-testid="admin-vidente-monitor" className="space-y-4">
      {/* Header + refresh */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-xl text-white">Monitor Vidente Virtual</h2>
          <p className="text-xs text-neutral-400 mt-1">Vista en tiempo real de todas las consultas y memorias espirituales.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="vidente-global-toggle"
            onClick={toggleGlobal}
            disabled={globalEnabled === null}
            title="Activa o desactiva el módulo Vidente Virtual para todos los usuarios"
            className={`text-xs px-3 py-2 rounded-full border flex items-center gap-1.5 font-semibold uppercase tracking-widest transition-colors ${
              globalEnabled === null
                ? "border-white/10 text-neutral-500"
                : globalEnabled
                ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20"
                : "border-red-500/50 text-red-300 bg-red-500/10 hover:bg-red-500/20"
            }`}
          >
            <Power className="w-3.5 h-3.5" />
            {globalEnabled === null ? "..." : globalEnabled ? "Módulo ACTIVO" : "Módulo APAGADO"}
          </button>
          <button
            data-testid="monitor-refresh"
            onClick={load}
            className="text-xs px-3 py-2 rounded-full border border-white/10 hover:border-[#d4af37]/40 text-neutral-300 hover:text-[#d4af37] flex items-center gap-1.5"
          >
            <RefreshCw className="w-3 h-3" /> Recargar
          </button>
        </div>
      </div>

      {globalEnabled === false && (
        <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="vidente-global-off-banner">
          🔴 El Vidente Virtual está <b>desactivado globalmente</b>: ningún usuario puede entrar aunque tenga tiempo asignado. Actívalo con el botón "Módulo".
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={<Users className="w-4 h-4" />} label="Habilitados" value={stats.enabled_count} accent="emerald" />
        <StatCard icon={<Sparkles className="w-4 h-4" />} label="Veces habilitados" value={stats.total_grants} accent="gold" />
        <StatCard icon={<MessageSquareText className="w-4 h-4" />} label="Sesiones totales" value={stats.total_sessions} accent="purple" />
        <StatCard icon={<Radio className="w-4 h-4" />} label="Activos ahora" value={stats.active_now} accent="red" />
      </div>

      {/* Users table */}
      {users.length === 0 ? (
        <div className="text-sm text-neutral-500 text-center py-10 border border-white/5 rounded-xl">
          Aún no hay usuarios con acceso al Vidente Virtual.
        </div>
      ) : (
        <div className="overflow-x-auto border border-white/5 rounded-xl">
          <table className="w-full text-xs">
            <thead className="bg-black/40 text-neutral-400 uppercase tracking-widest">
              <tr>
                <th className="p-3 text-left">Usuario</th>
                <th className="p-3 text-left">Estado</th>
                <th className="p-3 text-center">Habilitado #</th>
                <th className="p-3 text-center">Sesiones</th>
                <th className="p-3 text-center">⭐</th>
                <th className="p-3 text-center">Memoria</th>
                <th className="p-3 text-left">Última actividad</th>
                <th className="p-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {users.map((u) => (
                <tr
                  key={u.user_id}
                  data-testid={`monitor-user-${u.user_id}`}
                  className={`hover:bg-white/5 ${u.active_session_id ? "bg-red-500/[0.04]" : ""}`}
                >
                  <td className="p-3">
                    <div className="text-white font-semibold">{u.name || u.username || "—"}</div>
                    <div className="text-[10px] text-neutral-500">@{u.username} · {u.email}</div>
                  </td>
                  <td className="p-3">
                    {u.active_session_id ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold uppercase tracking-widest">
                        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> En vivo
                      </span>
                    ) : u.timer_state === "pending" ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-600/30 text-amber-300 text-[10px] font-bold uppercase tracking-widest border border-amber-500/40"
                        title="El contador iniciará con su primera interacción"
                      >
                        <Hourglass className="w-2.5 h-2.5" /> Pendiente {fmtMin(u.pending_minutes)}
                      </span>
                    ) : u.timer_state === "running" ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600/30 text-emerald-300 text-[10px] font-bold uppercase tracking-widest border border-emerald-500/40"
                        title={`Termina: ${new Date(u.vidente_access_until).toLocaleString()}`}
                      >
                        ⏱ Corriendo
                      </span>
                    ) : u.is_enabled_now ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600/30 text-emerald-300 text-[10px] font-bold uppercase tracking-widest border border-emerald-500/40">
                        {u.timer_state === "unlimited" ? "Ilimitado" : "Habilitado"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neutral-700/40 text-neutral-400 text-[10px] uppercase tracking-widest">
                        {u.timer_state === "expired" ? "Expirado" : "Inactivo"}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-center text-[#d4af37] font-bold">{u.grants_count}</td>
                  <td className="p-3 text-center">{u.total_sessions}</td>
                  <td className="p-3 text-center">
                    {u.avg_rating != null ? (
                      <span className="inline-flex items-center gap-0.5">
                        <Star className="w-3 h-3 text-[#d4af37] fill-[#d4af37]" />
                        <span className="text-[#d4af37] font-bold">{u.avg_rating}</span>
                        <span className="text-neutral-500">/{u.ratings_count}</span>
                      </span>
                    ) : <span className="text-neutral-600">—</span>}
                  </td>
                  <td className="p-3 text-center">
                    {u.has_profile ? <Brain className="w-3.5 h-3.5 text-purple-400 inline" /> : <span className="text-neutral-700">—</span>}
                  </td>
                  <td className="p-3 text-neutral-400">
                    {u.last_session_at ? new Date(u.last_session_at).toLocaleString() : "—"}
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                      <button
                        data-testid={`monitor-view-${u.user_id}`}
                        onClick={() => openDossier(u.user_id)}
                        title="Ver dossier completo (resúmenes, sesiones, memoria)"
                        className="text-xs px-2 py-1 rounded border border-white/10 hover:border-sky-500/40 text-sky-300 flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" /> Ver
                      </button>
                      <button
                        data-testid={`monitor-timer-reset-${u.user_id}`}
                        onClick={() => resetTimer(u)}
                        title="Reiniciar tiempo de acceso (el contador inicia con su primera interacción)"
                        className="text-xs px-2 py-1 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 flex items-center gap-1"
                      >
                        <RotateCcw className="w-3 h-3" /> Timer
                      </button>
                      {u.is_enabled_now && (
                        <button
                          data-testid={`monitor-timer-cancel-${u.user_id}`}
                          onClick={() => cancelTimer(u)}
                          title="Cancelar acceso ahora"
                          className="text-xs px-2 py-1 rounded border border-red-500/40 text-red-300 hover:bg-red-500/10 flex items-center gap-1"
                        >
                          <Ban className="w-3 h-3" /> Cancelar
                        </button>
                      )}
                      {u.has_profile && (
                        <button
                          data-testid={`monitor-reset-${u.user_id}`}
                          onClick={() => resetMemory(u.user_id)}
                          title="Reiniciar memoria espiritual"
                          className="text-xs px-2 py-1 rounded border border-purple-500/40 text-purple-300 hover:bg-purple-500/10 flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" /> Memoria
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Closed/expired sessions archive */}
      <VidenteClosedSessionsSection />

      {/* Dossier modal */}
      {selected && (
        <DossierModal
          loading={dossierLoading}
          data={dossier}
          onClose={() => { setSelected(null); setDossier(null); }}
          onResetMemory={(uid) => resetMemory(uid)}
          onDeleteSession={(sid, uid) => deleteSession(sid, uid)}
        />
      )}
    </div>
  );
}

function StatCard({ icon, label, value, accent }) {
  const colors = {
    emerald: "from-emerald-600/30 to-emerald-900/10 border-emerald-500/30 text-emerald-300",
    gold: "from-amber-500/30 to-amber-900/10 border-[#d4af37]/40 text-[#d4af37]",
    purple: "from-purple-600/30 to-purple-900/10 border-purple-500/30 text-purple-300",
    red: "from-red-600/30 to-red-900/10 border-red-500/30 text-red-300",
  };
  return (
    <div className={`bg-gradient-to-br ${colors[accent]} border rounded-xl p-3`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-80">
        {icon} {label}
      </div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

function DossierModal({ loading, data, onClose, onResetMemory, onDeleteSession }) {
  return (
    <div
      data-testid="dossier-overlay"
      className="fixed inset-0 z-[130] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl bg-gradient-to-b from-[#0d0a14] to-[#0a0a0a] border border-[#d4af37]/30 rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-neutral-400 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>

        {loading || !data ? (
          <div className="text-center py-12 text-neutral-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : (
          <DossierContent data={data} onResetMemory={onResetMemory} onDeleteSession={onDeleteSession} />
        )}
      </div>
    </div>
  );
}

function DossierContent({ data, onResetMemory, onDeleteSession }) {
  const { user, profile, sessions, ratings } = data;
  return (
    <div className="space-y-5 pr-2">
      <header>
        <h2 className="font-serif text-2xl text-white">{user.name || user.username}</h2>
        <p className="text-xs text-neutral-500 mt-1">@{user.username} · {user.email} · {user.country || "—"} · {user.phone || "—"}</p>
        <div className="flex items-center gap-3 mt-3 text-xs text-neutral-300 flex-wrap">
          <span className="px-2 py-1 rounded bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/30">
            Habilitado {user.grants_count} {user.grants_count === 1 ? "vez" : "veces"}
          </span>
          {user.vidente_access_until && (
            <span className="px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
              Acceso: {user.vidente_access_until === "unlimited" ? "ilimitado" : new Date(user.vidente_access_until).toLocaleString()}
            </span>
          )}
          {user.geo && (
            <a
              href={`https://www.google.com/maps?q=${user.geo.lat},${user.geo.lng}`}
              target="_blank" rel="noreferrer"
              className="px-2 py-1 rounded bg-sky-500/15 text-sky-300 border border-sky-500/30 inline-flex items-center gap-1"
            >
              <MapPin className="w-3 h-3" /> Ubicación
            </a>
          )}
        </div>
      </header>

      {/* Memory */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm uppercase tracking-widest text-purple-300 flex items-center gap-1.5"><Brain className="w-4 h-4" /> Memoria espiritual de CrisAngel Astral</h3>
          {profile && (
            <button
              onClick={() => onResetMemory(user.id)}
              className="text-xs px-2.5 py-1 rounded-full border border-red-500/40 text-red-300 hover:bg-red-500/10 flex items-center gap-1"
              data-testid="dossier-reset-memory"
            >
              <Trash2 className="w-3 h-3" /> Reiniciar memoria
            </button>
          )}
        </div>
        {!profile ? (
          <div className="text-xs text-neutral-500 italic">Cristopher aún no ha extraído memoria de este usuario.</div>
        ) : (
          <div className="space-y-2 text-xs">
            {profile.full_name && <Row k="Nombre completo" v={profile.full_name} />}
            {profile.birthdate && <Row k="Fecha de nacimiento" v={`${profile.birthdate} ${profile.zodiac_sign ? "(" + profile.zodiac_sign + ")" : ""}`} />}
            {profile.emotional_state && <Row k="Estado emocional" v={profile.emotional_state} />}
            {profile.last_summary && <Row k="Resumen última sesión" v={profile.last_summary} />}
            {(profile.motivos || []).length > 0 && <Row k="Motivos" v={profile.motivos.join(" · ")} />}
            {(profile.facts_learned || []).length > 0 && (
              <Bullets title="Datos aprendidos" items={profile.facts_learned} />
            )}
            {(profile.relationships || []).length > 0 && (
              <Bullets title="Personas mencionadas" items={profile.relationships} />
            )}
            {(profile.predictions_made || []).length > 0 && (
              <Bullets title="Predicciones hechas" items={profile.predictions_made} />
            )}
          </div>
        )}
      </section>

      {/* Sessions */}
      <section>
        <h3 className="text-sm uppercase tracking-widest text-[#d4af37] mb-2 flex items-center gap-1.5"><MessageSquareText className="w-4 h-4" /> Sesiones ({sessions.length})</h3>
        {sessions.length === 0 ? (
          <div className="text-xs text-neutral-500 italic">Sin sesiones todavía.</div>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => (
              <details key={s.id} className="bg-black/30 border border-white/5 rounded-lg group">
                <summary className="cursor-pointer p-3 flex items-center gap-3 flex-wrap">
                  <span className={`w-2 h-2 rounded-full ${s.status === "active" ? "bg-emerald-400 animate-pulse" : s.status === "ended_by_admin" ? "bg-red-500" : "bg-neutral-600"}`} />
                  <span className="text-xs text-neutral-300 font-mono">{new Date(s.started_at).toLocaleString()}</span>
                  <span className="text-[10px] text-neutral-500 uppercase">{s.status}</span>
                  <span className="text-[10px] text-neutral-500 ml-auto">{s.messages_count} msgs · {(s.phases || []).length} tiradas</span>
                  <button
                    data-testid={`dossier-delete-session-${s.id}`}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDeleteSession?.(s.id, user.id); }}
                    title="Eliminar esta sesión permanentemente"
                    className="text-[10px] px-2 py-0.5 rounded border border-red-500/40 text-red-300 hover:bg-red-500/10 flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> Eliminar
                  </button>
                </summary>
                <div className="px-3 pb-3 space-y-2 text-xs">
                  {(s.messages || []).slice(-50).map((m, i) => (
                    <div key={i} className={`p-2 rounded ${m.role === "user" ? "bg-blue-500/10 border-l-2 border-blue-400/50" : m.role === "system" ? "bg-amber-500/10 border-l-2 border-amber-400/50" : "bg-purple-500/10 border-l-2 border-purple-400/50"}`}>
                      <div className="text-[9px] uppercase tracking-widest opacity-60 mb-0.5">{m.role} · {m.at ? new Date(m.at).toLocaleTimeString() : ""}</div>
                      <div className="whitespace-pre-wrap break-words">{m.content}</div>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        )}
      </section>

      {/* Ratings */}
      {ratings.current.length > 0 && (
        <section>
          <h3 className="text-sm uppercase tracking-widest text-[#d4af37] mb-2 flex items-center gap-1.5"><Star className="w-4 h-4" /> Calificaciones</h3>
          <div className="space-y-2">
            {ratings.current.map((r, i) => (
              <div key={i} className="bg-black/30 border border-white/5 rounded-lg p-3 text-xs">
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map(n => <Star key={n} className={`w-3 h-3 ${n <= r.stars ? "text-[#d4af37] fill-[#d4af37]" : "text-neutral-700"}`} />)}
                  <span className="text-[#d4af37] ml-1">{r.stars}/5</span>
                  <span className="text-neutral-500 ml-auto">{r.updated_at ? new Date(r.updated_at).toLocaleString() : ""}</span>
                </div>
                {r.comment && <div className="text-neutral-300 mt-1">"{r.comment}"</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Grants history */}
      {(user.grants_history || []).length > 0 && (
        <section>
          <h3 className="text-sm uppercase tracking-widest text-[#d4af37] mb-2 flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Habilitaciones ({(user.grants_history || []).length})</h3>
          <div className="space-y-1 text-xs">
            {(user.grants_history || []).slice(-20).reverse().map((g, i) => (
              <div key={i} className="flex items-center gap-2 text-neutral-400">
                <Hourglass className="w-3 h-3" />
                <span>{g.at ? new Date(g.at).toLocaleString() : ""}</span>
                <span className="text-[#d4af37]">· {g.duration}</span>
                {g.by && <span className="text-neutral-600">· por @{g.by}</span>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-2">
      <span className="text-neutral-500 min-w-[120px]">{k}:</span>
      <span className="text-neutral-200">{v}</span>
    </div>
  );
}

function Bullets({ title, items }) {
  return (
    <div>
      <div className="text-neutral-500 mb-1">{title}:</div>
      <ul className="space-y-0.5 pl-2">
        {items.map((it, i) => (
          <li key={i} className="text-neutral-200">• {it}</li>
        ))}
      </ul>
    </div>
  );
}

